import { useEffect, useRef, useState } from 'react';
import { Notification } from '@arco-design/web-react';
import { completeOneTapSignIn } from '../../services/auth.service';
import { storageService } from '../../services/storage.service';
import { CMS_BASE_URL } from '../../config/api.config';
import AuthPendingOverlay from '../AuthPendingOverlay/AuthPendingOverlay';

interface GoogleOneTapProps {
  onLoginSuccess: () => void;
}

/*
 * Google One Tap, run inside a page on the CMS (DECISIONS D6).
 *
 * Google allows One Tap only on addresses registered in Google Cloud, and a
 * new salon's address can't be registered automatically. In Google's
 * "intermediate iframe" mode only the frame's address is registered — the
 * CMS, once — and this page just embeds it. Google supports that for
 * same-site pages only: every <salon>.mypetgroomer.com.au storefront is
 * same-site with the CMS. Anywhere else (local dev, *.pages.dev, a salon's
 * own domain) the frame isn't embedded and the "Continue with Google" button
 * is the way in.
 *
 * The frame does the sign-in and posts the session here; a message counts
 * only if it comes from the CMS's origin.
 */
const INTERMEDIATE_SCRIPT = 'https://accounts.google.com/gsi/intermediate';

const cmsOrigin = (() => {
  try {
    return new URL(CMS_BASE_URL).origin;
  } catch {
    return '';
  }
})();

/** Same site as the CMS: cms.example.com and <salon>.example.com share "example.com". */
const sameSiteAsCms = (): boolean => {
  if (!cmsOrigin || window.location.protocol !== 'https:') return false;
  const cmsHost = new URL(cmsOrigin).hostname;
  const zone = cmsHost.split('.').slice(1).join('.');
  return zone.includes('.') && window.location.hostname.endsWith(`.${zone}`);
};

const GoogleOneTap: React.FC<GoogleOneTapProps> = ({ onLoginSuccess }) => {
  const onLoginSuccessRef = useRef(onLoginSuccess);
  // Refs, not state, so the message listener always sees the current value
  // (and StrictMode's double effect in dev shares them).
  const cancelledRef = useRef(false);
  const signingInRef = useRef(false);
  /*
   * The prompt vanishes the instant it's clicked, and everything after it —
   * exchanging the credential, then reloading — used to happen with no UI at
   * all. The page just sat there, which reads as broken, so customers clicked
   * again. This drives the overlay that covers that gap.
   */
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [embed] = useState(sameSiteAsCms);

  useEffect(() => {
    onLoginSuccessRef.current = onLoginSuccess;
  }, [onLoginSuccess]);

  useEffect(() => {
    if (!embed) return;
    cancelledRef.current = false;

    const fail = () => {
      signingInRef.current = false;
      setIsSigningIn(false);
      // A notification, not the overlay: after a failure they need the page
      // back to try again.
      Notification.error({
        title: 'Sign-in failed',
        content: "We couldn't sign you in with Google. Please try again.",
      });
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== cmsOrigin) return;
      const data = event.data;
      if (!data || data.type !== 'mpg-one-tap' || cancelledRef.current) return;

      if (data.stage === 'started') {
        signingInRef.current = true;
        setIsSigningIn(true);
        return;
      }
      if (data.stage === 'failed') {
        console.error('[GoogleOneTap] Login failed in the sign-in frame');
        fail();
        return;
      }
      if (data.stage === 'done') {
        try {
          const authData = completeOneTapSignIn(data);
          const userProfile = {
            name: `${authData.user.firstName} ${authData.user.lastName}`.trim(),
            email: authData.user.email,
            picture: authData.user.picture,
          };
          storageService.setItem('user', JSON.stringify(userProfile));
          onLoginSuccessRef.current();
          // The overlay deliberately stays up through the reload — dropping
          // it first would flash the signed-out page for a moment.
          window.location.reload();
        } catch (err) {
          console.error('[GoogleOneTap] Login failed:', err);
          fail();
        }
      }
    };

    window.addEventListener('message', onMessage);
    // Google's script finds the div below and builds the frame from it.
    if (!document.querySelector(`script[src="${INTERMEDIATE_SCRIPT}"]`)) {
      const script = document.createElement('script');
      script.src = INTERMEDIATE_SCRIPT;
      script.async = true;
      document.head.appendChild(script);
    }

    return () => {
      cancelledRef.current = true;
      window.removeEventListener('message', onMessage);
    };
  }, [embed]);

  if (!embed) return null;
  const frameSrc = `${CMS_BASE_URL}/api/google-auth/one-tap-frame?parent=${encodeURIComponent(window.location.origin)}`;

  return (
    <>
      <div id="g_id_intermediate_iframe" data-src={frameSrc} />
      <AuthPendingOverlay isVisible={isSigningIn} />
    </>
  );
};

export default GoogleOneTap;
